import { useMemo, useState } from 'react'
import { flattenStructure } from '@kronos/domain'
import type { StructureNode } from '@kronos/domain'
import { allAssemblies, collapsedBeyond, keyMove, shownTree } from './visible'
import type { Move, Shown } from './visible'

/**
 * What is open, what is selected, and the moves between them — the state the
 * diagram and the outline share, so switching views loses nothing.
 *
 * The collapsed set is remembered *per root*: drawing a different item starts
 * with its own first two levels open rather than inheriting keys that mean
 * nothing in it. Changing the quantity keeps it, because the keys do not move.
 */
export interface TreeState {
  readonly shown: Shown | null
  readonly selected: string | null
  /** The selected node and its parent, if the selection is still in the tree. */
  readonly chosen: { readonly node: StructureNode; readonly parent: StructureNode | null } | undefined
  readonly select: (key: string | null) => void
  readonly toggle: (key: string) => void
  readonly expandAll: () => void
  readonly collapseAll: () => void
  /** Apply an arrow key; returns the key focus moves to. */
  readonly move: (from: string, m: Move) => string
  /** Select the first node of an item, opening every branch on the way to it. */
  readonly pick: (typeId: string) => void
}

export function useTreeState(root: StructureNode | null): TreeState {
  const [selected, setSelected] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState<{ root: string; keys: ReadonlySet<string> } | null>(null)

  const closed = useMemo<ReadonlySet<string>>(
    () => (root === null ? new Set() : collapsed?.root === root.key ? collapsed.keys : collapsedBeyond(root, 2)),
    [root, collapsed],
  )
  const shown = useMemo(() => (root === null ? null : shownTree(root, closed)), [root, closed])
  const index = useMemo(() => {
    const out = new Map<string, { node: StructureNode; parent: StructureNode | null }>()
    const walk = (n: StructureNode, parent: StructureNode | null): void => {
      out.set(n.key, { node: n, parent })
      for (const c of n.children) walk(c, n)
    }
    if (root !== null) walk(root, null)
    return out
  }, [root])

  const setClosed = (keys: ReadonlySet<string>): void => {
    if (root !== null) setCollapsed({ root: root.key, keys })
  }
  const toggle = (key: string): void => {
    const next = new Set(closed)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setClosed(next)
  }

  return {
    shown,
    selected,
    chosen: selected === null ? undefined : index.get(selected),
    select: setSelected,
    toggle,
    expandAll: () => setClosed(new Set()),
    collapseAll: () => {
      if (root !== null) setClosed(allAssemblies(root))
    },
    move: (from, m) => {
      if (shown === null) return from
      const r = keyMove(shown, from, m)
      if (r.toggle !== undefined) toggle(from)
      return r.focus
    },
    pick: (typeId) => {
      if (root === null) return
      const target = flattenStructure(root).find((n) => n.typeId === typeId && n.depth > 0)
      if (target === undefined) return
      const next = new Set(closed)
      for (let p = index.get(target.key)?.parent ?? null; p !== null; p = index.get(p.key)?.parent ?? null) {
        next.delete(p.key)
      }
      setClosed(next)
      setSelected(target.key)
    },
  }
}
